import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getVendorServices } from '../services/vendor';
import { useAuth } from './AuthContext';

const ServiceContext = createContext(null);
const OPEN_SUBSCRIPTION_STATUSES = new Set(['ACTIVE', 'TRIALING']);

const hasOpenSubscription = (service) => {
  const statusCandidates = [
    service?.subscription?.status,
    service?.status,
    service?.billingGate?.status,
  ];
  return statusCandidates.some((status) => OPEN_SUBSCRIPTION_STATUSES.has(String(status || '').toUpperCase()));
};

const isOpenService = (service) => {
  if (service?.domainGate) return false;
  if (service?.activationStatus !== 'ACTIVE') return false;
  return !service?.billingGate || hasOpenSubscription(service);
};

export const ServiceProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadServices = useCallback(async () => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    if (!isAuthenticated) {
      setServices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getVendorServices();
      setServices(response?.services || []);
    } catch {
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const activeServiceCodes = useMemo(
    () =>
      new Set(
        services
          .filter(isOpenService)
          .map((service) => service.code),
      ),
    [services],
  );

  const value = useMemo(
    () => ({
      services,
      loading: authLoading || loading,
      reloadServices: loadServices,
      isServiceActive: (code) => activeServiceCodes.has(String(code || '').toUpperCase()),
      isServiceEnabled: (code) => {
        const service = services.find((item) => item.code === String(code || '').toUpperCase());
        return service?.activationStatus === 'ACTIVE';
      },
      getService: (code) => services.find((service) => service.code === String(code || '').toUpperCase()) || null,
      getServiceAccess: (code) => {
        const normalizedCode = String(code || '').toUpperCase();
        const service = services.find((item) => item.code === normalizedCode) || null;
        const enabled = service?.activationStatus === 'ACTIVE';
        const active = isOpenService(service);
        return {
          service,
          enabled,
          active,
          billingGate: active ? null : service?.billingGate || null,
          subscription: service?.subscription || null,
        };
      },
    }),
    [activeServiceCodes, loadServices, loading, services],
  );

  return <ServiceContext.Provider value={value}>{children}</ServiceContext.Provider>;
};

export const useServices = () => useContext(ServiceContext);
